/**
 * `DateField`（LeapWorldTwin-145）：一个输入框同时支持键盘直接输入（`2026-09-06`/`9/6`/`0906`/
 * `今天`/`+7`）与日历选择，移动端回退原生 `<input type="date">`。
 * 来源：dzy 2026-09-06「日期填写需要一个个打年月日，比较反人类」——替换全仓裸 `type="date"` /
 * `datetime-local` / 自写年月日拆分输入（见 `docs/architecture/11-ui-kit.md` 控件表）。
 *
 * 契约：受控组件，`value`/`onChange` 都是 `YYYY-MM-DD`（或空串），与原来的 `<input type="date">`
 * 同形状，替换点改动面最小——**但 `onChange` 传的是规范化后的字符串本身，不是事件对象**
 * （原写法 `onChange={(e) => set(e.target.value)}` 改成 `onChange={(v) => set(v)}`）。
 * 解析全部委派给 `@bf/format` 的 `parseDateInput`，本文件只管渲染与交互，不重复日历数学。
 *
 * 弹层复用 `useOverlay`（Escape / 遮罩关闭 / 焦点管理），不新写浮层逻辑；定位与外点关闭抄
 * `DropdownMenu.tsx` 的锚定浮层写法（相对定位容器 + 绝对定位面板 + document mousedown 监听）。
 *
 * 移动端回退（`≤768px` 或触屏）：日历按钮改为触发一个视觉隐藏的原生 `<input type="date">`
 * 的 `showPicker()`（不支持时退化 `.click()`），复用系统选择器，输入框本身仍可键入。
 */
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { formatParsedDatePreview, parseDateInput, toISODateOnly } from '@bf/format'
import { Button } from './Button'
import { controlClassName } from './control-classes'
import { CalendarDays } from './icons'
import { useOverlay } from './use-overlay'

export type DateFieldProps = {
  value: string
  onChange: (value: string) => void
  min?: string
  max?: string
  placeholder?: string
  disabled?: boolean
  error?: boolean
  required?: boolean
  name?: string
  className?: string
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  /** 不经 <Field> 包裹的独立用法（如筛选栏）：直接给输入框可达名称 */
  'aria-label'?: string
  /** 日历选值与手输共用：受控值提交时通知表单脏标记，不派发 DOM 事件 */
  onDirty?: () => void
}

/** `≤768px` 与「粗指针」（触屏）任一命中即算移动端——桌面鼠标 + 窄窗口也算移动端，纯触屏平板同样算。 */
function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia('(max-width: 768px), (pointer: coarse)').matches
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia('(max-width: 768px), (pointer: coarse)')
    const onChange = () => setMobile(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  return mobile
}

function isoToLocalDate(iso: string): Date | null {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  date.setHours(0, 0, 0, 0)
  return date
}

const WEEKDAY_HEADS = ['日', '一', '二', '三', '四', '五', '六']

function MonthCalendar({
  monthCursor,
  selected,
  min,
  max,
  onSelect,
  onMonthChange
}: {
  monthCursor: Date
  selected: Date | null
  min: Date | null
  max: Date | null
  onSelect: (date: Date) => void
  onMonthChange: (date: Date) => void
}) {
  const gridRef = useRef<HTMLDivElement>(null)
  const year = monthCursor.getFullYear()
  const month = monthCursor.getMonth()
  const [focusedDay, setFocusedDay] = useState(() => (selected && selected.getFullYear() === year && selected.getMonth() === month ? selected.getDate() : 1))

  const cells = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1)
    const startOffset = firstOfMonth.getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const list: { date: Date; inMonth: boolean }[] = []
    for (let i = 0; i < startOffset; i++) {
      const d = new Date(year, month, 1 - (startOffset - i))
      list.push({ date: d, inMonth: false })
    }
    for (let day = 1; day <= daysInMonth; day++) {
      list.push({ date: new Date(year, month, day), inMonth: true })
    }
    while (list.length % 7 !== 0) {
      const last = list[list.length - 1]!.date
      list.push({ date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1), inMonth: false })
    }
    return list
  }, [year, month])

  useEffect(() => {
    gridRef.current?.querySelector<HTMLElement>('[data-day-focus="true"]')?.focus()
  }, [focusedDay, year, month])

  const isDisabled = (date: Date) => (min && date < min) || (max && date > max)

  const moveFocus = (deltaDays: number) => {
    const current = new Date(year, month, focusedDay)
    current.setDate(current.getDate() + deltaDays)
    if (current.getFullYear() !== year || current.getMonth() !== month) {
      onMonthChange(new Date(current.getFullYear(), current.getMonth(), 1))
    }
    setFocusedDay(current.getDate())
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault()
        moveFocus(-1)
        return
      case 'ArrowRight':
        event.preventDefault()
        moveFocus(1)
        return
      case 'ArrowUp':
        event.preventDefault()
        moveFocus(-7)
        return
      case 'ArrowDown':
        event.preventDefault()
        moveFocus(7)
        return
      case 'Enter':
      case ' ': {
        event.preventDefault()
        const date = new Date(year, month, focusedDay)
        if (!isDisabled(date)) onSelect(date)
        return
      }
      default:
        return
    }
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <Button
          variant="icon"
          size="sm"
          aria-label="上一月"
          onClick={() => onMonthChange(new Date(year, month - 1, 1))}
        >
          ‹
        </Button>
        <span className="text-sm font-medium text-[var(--color-ink)]">{year}年{month + 1}月</span>
        <Button
          variant="icon"
          size="sm"
          aria-label="下一月"
          onClick={() => onMonthChange(new Date(year, month + 1, 1))}
        >
          ›
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-[var(--color-ink-secondary)]">
        {WEEKDAY_HEADS.map((w) => (
          <span key={w} className="py-1">{w}</span>
        ))}
      </div>
      <div ref={gridRef} role="grid" aria-label="日历" className="grid grid-cols-7 gap-1" onKeyDown={onKeyDown}>
        {cells.map(({ date, inMonth }) => {
          const disabled = Boolean(isDisabled(date))
          const isSelected = selected ? date.getTime() === selected.getTime() : false
          const isFocusTarget = date.getDate() === focusedDay && inMonth
          return (
            <button
              key={date.toISOString()}
              type="button"
              role="gridcell"
              data-day-focus={isFocusTarget ? 'true' : undefined}
              tabIndex={isFocusTarget ? 0 : -1}
              disabled={disabled}
              aria-selected={isSelected}
              aria-current={date.toDateString() === new Date().toDateString() ? 'date' : undefined}
              className={`min-h-8 rounded-[var(--radius-sm)] py-1 text-xs tabular-nums transition-colors focus:outline-none focus-visible:ring-[2px] focus-visible:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:opacity-35 ${
                !inMonth ? 'text-[var(--color-faint)]' : 'text-[var(--color-ink)]'
              } ${isSelected ? 'bg-[var(--color-primary)] text-[var(--color-on-primary)]' : 'hover:bg-[var(--color-soft)]'}`}
              onClick={() => {
                if (inMonth) setFocusedDay(date.getDate())
                if (!disabled) onSelect(date)
              }}
            >
              {date.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function DateField({
  value,
  onChange,
  min,
  max,
  placeholder = '如 2026-09-06、9/6、今天、+7',
  disabled,
  error,
  required,
  name,
  className = '',
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-label': ariaLabel,
  onDirty
}: DateFieldProps) {
  const [text, setText] = useState(value || '')
  const [open, setOpen] = useState(false)
  const [monthCursor, setMonthCursor] = useState(() => isoToLocalDate(value) ?? new Date())
  const rootRef = useRef<HTMLDivElement>(null)
  const nativeRef = useRef<HTMLInputElement>(null)
  const isMobile = useIsMobile()
  const reportDirty = () => onDirty?.()
  const previewId = useId()
  const panelRef = useOverlay<HTMLDivElement>(open, () => setOpen(false))

  // 外部 value 变化（受控更新、切换记录等）时同步输入框文字，避免打字过程中被覆盖——
  // 只在未聚焦时同步，防止用户正在敲字时被 value 回写打断。
  useEffect(() => {
    if (document.activeElement?.closest('[data-datefield-root]') === rootRef.current) return
    setText(value || '')
    const parsedFromValue = isoToLocalDate(value)
    if (parsedFromValue) setMonthCursor(parsedFromValue)
  }, [value])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const parsed = useMemo(() => parseDateInput(text), [text])
  const selectedDate = isoToLocalDate(value)
  const minDate = min ? isoToLocalDate(min) : null
  const maxDate = max ? isoToLocalDate(max) : null

  const commit = (date: Date) => {
    const iso = toISODateOnly(date)
    setText(iso)
    onChange(iso)
    setMonthCursor(date)
    setOpen(false)
    reportDirty()
  }

  const onInputChange = (next: string) => {
    setText(next)
    reportDirty()
  }

  const onInputBlur = () => {
    if (!text.trim()) {
      onChange('')
      reportDirty()
      return
    }
    if (parsed.date) {
      const iso = toISODateOnly(parsed.date)
      setText(iso)
      onChange(iso)
      setMonthCursor(parsed.date)
      reportDirty()
    }
    // 解析失败时不覆盖用户输入，让下方回显的红字提示留着，方便用户改
  }

  const openCalendar = () => {
    if (isMobile) {
      const native = nativeRef.current
      if (!native) return
      if (typeof (native as HTMLInputElement & { showPicker?: () => void }).showPicker === 'function') {
        ;(native as HTMLInputElement & { showPicker: () => void }).showPicker()
      } else {
        native.click()
      }
      return
    }
    if (selectedDate) setMonthCursor(selectedDate)
    setOpen((current) => !current)
  }

  const previewNode: ReactNode = text.trim()
    ? parsed.date
      ? <span id={previewId} className="mt-1 block text-[11px] text-[var(--color-ink-secondary)]">→ {formatParsedDatePreview(parsed.date)}</span>
      : <span id={previewId} className="mt-1 block text-[11px] text-[var(--color-danger)]">无法识别「{text}」，试试 2026-09-06 / 9/6 / 今天 / +7</span>
    : null

  return (
    <div ref={rootRef} data-datefield-root className={`relative ${className}`.trim()}>
      <div className="flex items-center gap-1">
        <input
          id={id}
          name={name}
          type="text"
          inputMode="text"
          autoComplete="off"
          disabled={disabled}
          required={required}
          value={text}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-describedby={[ariaDescribedBy, previewNode ? previewId : undefined].filter(Boolean).join(' ') || undefined}
          aria-invalid={ariaInvalid || error || undefined}
          className={`${controlClassName('', Boolean(error) || (Boolean(text.trim()) && !parsed.date))} max-md:min-h-11`}
          onChange={(event) => onInputChange(event.target.value)}
          onBlur={onInputBlur}
        />
        <Button
          type="button"
          variant="icon"
          disabled={disabled}
          aria-label="打开日历"
          aria-haspopup={isMobile ? undefined : 'dialog'}
          aria-expanded={isMobile ? undefined : open}
          onClick={openCalendar}
        >
          <CalendarDays size={16} aria-hidden="true" />
        </Button>
      </div>
      {previewNode}
      {/* 移动端回退：视觉隐藏但可交互的原生日期选择器，键盘/朗读器仍可直达 */}
      <input
        ref={nativeRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
        value={value || ''}
        min={min}
        max={max}
        onChange={(event) => {
          const next = event.target.value
          setText(next)
          onChange(next)
          const d = isoToLocalDate(next)
          if (d) setMonthCursor(d)
          reportDirty()
        }}
      />
      {open && !isMobile ? (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-label="选择日期"
          className="absolute left-0 top-full z-50 mt-1 w-64 rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-panel)] p-3 shadow-[var(--shadow-md)]"
        >
          <MonthCalendar
            monthCursor={monthCursor}
            selected={selectedDate}
            min={minDate}
            max={maxDate}
            onSelect={commit}
            onMonthChange={setMonthCursor}
          />
        </div>
      ) : null}
    </div>
  )
}
